import {
    ChangeEvent,
    FormEvent,
    useCallback,
    useEffect,
    useState,
} from 'react';
import { useObjectSelected } from '@domain/object-selected';
import { ActionCreator } from '@shared/redux';
import { DefaultRootState, useDispatch, useSelector } from 'react-redux';

export const useFormFields = ({ initialData, onSubmit }) => {
    const [fields, setFields] = useState(initialData);

    useEffect(() => {
        setFields(initialData);
    }, [initialData]);

    const handleSubmit = useCallback((e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        onSubmit(fields);
    }, [fields, onSubmit]);

    const handleFieldChange = useCallback((name: string) => (e: ChangeEvent<HTMLInputElement>) => {
        setFields({ ...fields, [name]: e.target.value });
    }, [fields]);

    return {
        fields,
        handleFieldChange,
        handleSubmit,
    };
};

interface IntegrationState {
    loading: boolean
    submitting: boolean
    integrationData: string | any
}

interface IntegrationHookFactory extends IntegrationState{
    save: (params: any) => void
    loadInitialData: () => void
}

interface IntegrationHookFactoryActions {
    saveAction:ActionCreator<any>
    loadAction:ActionCreator<{clientId: string}>
    stateSelector:(state:DefaultRootState) => IntegrationState
}

export const integrationHookFactory = ({
    saveAction, loadAction, stateSelector,
}:IntegrationHookFactoryActions) => (): IntegrationHookFactory => {
    const dispatch = useDispatch();
    const integrationState = useSelector(stateSelector);
    const { clientSelected } = useObjectSelected();

    const save = (params) => {
        dispatch(saveAction({ ...params, clientId: clientSelected.id }));
    };

    const loadInitialData = useCallback(
        () => {
            dispatch(loadAction({ clientId: clientSelected.id }));
        }, [clientSelected.id, dispatch],
    );

    return {
        ...integrationState,
        save,
        loadInitialData,
    };
};
